/**
 * What filing this expense would do to everybody, drawn under the split
 * editor and redrawn on every keystroke: what each person put in, what their
 * share is, and where that leaves them.
 *
 * Everything it says comes from `previewSettlement` in `src/lib/split-draft.ts`
 * — the same payer and split readings the sheet sends — and its words are
 * `describeStake`'s and `describePerson`'s, the ones the group's list and the
 * balances use, so nothing here can preview a sentence those screens would
 * not say once it is filed.
 *
 * While the payers or the split do not add up, it says nothing: the editors
 * above are already saying what is wrong, and a preview of numbers that are
 * about to change is a second, quieter version of the same complaint.
 */

import type { Member } from '../api'
import { formatMoney } from '../lib/format'
import { previewSettlement, type ExpenseSheet } from '../lib/split-draft'
import { Amount } from './Amount'

export function SettlementPreview({
  sheet,
  currency,
  myId,
  members,
}: {
  sheet: ExpenseSheet
  currency: string
  myId: string
  /** In the group's own order — the order an odd cent is broken by. */
  members: readonly Member[]
}) {
  const memberOrder = members.map((member) => member.userId)
  const displayName = (userId: string) =>
    members.find((member) => member.userId === userId)?.displayName ?? 'Somebody'

  const preview = previewSettlement({ sheet, myId, memberOrder, nameOf: displayName, currency })
  if (preview.error !== null) return null

  return (
    <section className="form__row settlement" aria-label="What this expense does to balances">
      <span className="sheet__label">After this expense</span>

      {preview.owing.length > 0 && (
        <ul className="settlement__owing" role="status">
          {preview.owing.map((sentence) => (
            <li key={sentence}>{sentence}.</li>
          ))}
        </ul>
      )}

      <ul className="settlement__rows">
        {preview.rows.map((row) => (
          <li key={row.userId} className="settlement__row">
            <span className="settlement__who">
              {row.userId === myId ? `${displayName(row.userId)} (you)` : displayName(row.userId)}
              <span className="muted note">
                paid {formatMoney(row.paidCents, currency)} · owes{' '}
                {formatMoney(row.owesCents, currency)}
              </span>
            </span>
            <Amount
              direction={row.direction}
              label={row.label}
              cents={row.netCents}
              currency={currency}
              count={false}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}
